import { ReactNode } from "react";
import { TableHeaderType, FilterType, isMultiValueFilter } from "../types";

/**
 * Utility functions for formatting filter conditions consistently across components
 */

/**
 * Format a filter condition as a React node with proper styling
 * @param searchKey - The search key from saved filter data
 * @param value - The filter value(s)
 * @param headers - Table headers containing filter configuration
 * @param currencySymbol - Currency symbol to display for cost fields
 * @returns React node with formatted condition or null if not found
 */
export const formatFilterCondition = (
  searchKey: string,
  value: string | string[],
  headers: TableHeaderType[],
  currencySymbol: string
): ReactNode => {
  // Find the matching header by searchKey
  const header = headers.find(h => h.filter?.searchKey === searchKey);
  if (!header?.filter) return null;

  const fieldLabel = typeof header.label === "string" ? header.label : header.uid;
  const { filterType } = header.filter;
  const filterMode = "filterMode" in header.filter ? header.filter.filterMode : undefined;

  // Format based on filter type and mode to match FilterSection components
  if (filterType === FilterType.TEXT) {
    let operator = "is greater than";
    let displayValue = String(value);
    const shouldShowCurrency = fieldLabel.toLowerCase().includes("cost");

    if (filterMode === "gt") {
      operator = "is greater than";
    } else if (filterMode === "lt") {
      operator = "is less than";
    } else if (filterMode === "bt" && Array.isArray(value) && value.length === 2) {
      operator = "is between";
      displayValue = shouldShowCurrency
        ? `${currencySymbol}${value[0]} - ${currencySymbol}${value[1]}`
        : `${value[0]} - ${value[1]}`;
    }

    if (shouldShowCurrency && filterMode !== "bt") {
      displayValue = `${currencySymbol}${displayValue}`;
    }

    return (
      <>
        <span className="al-text-brand">{fieldLabel}</span> {operator}{" "}
        <span className="al-text-brand">{displayValue}</span>
      </>
    );
  }

  if (isMultiValueFilter(filterType)) {
    // All dropdown types use "contains" operator as per FilterDropdownInput
    if (Array.isArray(value)) {
      return (
        <>
          <span className="al-text-brand">{fieldLabel}</span>
          {" contains "}
          {value.map((val, index) => (
            <>
              <span key={`value-${index}`} className="al-text-brand">
                {val}
              </span>
              {index < value.length - 1 && " OR "}
            </>
          ))}
        </>
      );
    }
    return (
      <>
        <span className="al-text-brand">{fieldLabel}</span>
        {" contains "}
        <span className="al-text-brand">{String(value)}</span>
      </>
    );
  }

  if (filterType === FilterType.DROPDOWN_SINGLE) {
    return (
      <>
        <span className="al-text-brand">{fieldLabel}</span>
        {" contains "}
        <span className="al-text-brand">{String(value)}</span>
      </>
    );
  }

  // Fallback
  const displayValue = Array.isArray(value) ? value.join(", ") : String(value);
  return (
    <>
      <span className="al-text-brand">{fieldLabel}</span>
      {": "}
      <span className="al-text-brand">{displayValue}</span>
    </>
  );
};

/**
 * Get available operator options for a filter type
 * @param filterType - The type of filter
 * @param filterMode - The mode of filter (for text filters)
 * @returns Array of operator options with value and label
 */
export const getOperatorOptions = (
  filterType: FilterType,
  filterMode?: "bt" | "gt" | "lt" | { value: string } | string
): Array<{ value: string; label: string }> => {
  if (filterType === FilterType.TEXT) {
    if (filterMode) {
      return [
        { value: "gt", label: "greater than" },
        { value: "lt", label: "less than" },
      ];
    }
    return [{ value: "gt", label: "greater than" }];
  }

  if (filterType === FilterType.DROPDOWN_SINGLE) {
    return [{ value: "is", label: "is" }];
  }

  // For multi-select dropdowns
  return [{ value: "contains", label: "contains" }];
};

/**
 * Get the operator text for a given filter configuration
 * @param filterType - The type of filter
 * @param filterMode - The mode of filter (for text filters)
 * @returns The operator text
 */
export const getFilterOperator = (
  filterType: FilterType,
  filterMode?: "bt" | "gt" | "lt" | { value: string } | string
): string => {
  // Handle filterMode if it's an object
  const mode = typeof filterMode === "object" ? filterMode?.value : filterMode;

  if (filterType === FilterType.TEXT) {
    switch (mode) {
      case "gt":
        return "is greater than";
      case "lt":
        return "is less than";
      case "bt":
        return "is between";
      default:
        return "is greater than";
    }
  }

  if (filterType === FilterType.DROPDOWN_SINGLE) {
    return "is";
  }

  if (isMultiValueFilter(filterType)) {
    return "contains";
  }

  return "equals";
};

/**
 * Format multiple filter conditions as React nodes
 * @param filterData - Object containing filter data
 * @param headers - Table headers containing filter configuration
 * @param currencySymbol - Currency symbol to display for cost fields
 * @param connector - Logical connector between conditions (default: "AND")
 * @returns Array of React nodes with conditions and connectors
 */
export const formatMultipleConditions = (
  filterData: Record<string, unknown>,
  headers: TableHeaderType[],
  currencySymbol: string,
  connector: string = "AND"
): ReactNode[] => {
  const conditionEntries = Object.entries(filterData)
    .map(([searchKey, value]) =>
      formatFilterCondition(searchKey, value as string | string[], headers, currencySymbol)
    )
    .filter((condition): condition is ReactNode => condition !== null);

  const result: ReactNode[] = [];
  conditionEntries.forEach((condition, index) => {
    if (index > 0) {
      result.push(
        <span key={`connector-${index}`} className="al-text-sm  al-mx-2">
          {connector}
        </span>
      );
    }
    result.push(<span key={`condition-${index}`}>{condition}</span>);
  });

  return result;
};
